import { useCallback, useEffect, useLayoutEffect, useRef } from 'react'
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'

// A list beside the one thing picked from it, as the whole page: the Moments
// and Users pages' layout.
//
// On a desktop-wide window it fills the window under the shell, so nothing
// sits below the panes: the header (the title, the filters, the summary) is
// pinned, and under it the list and the picked item's pane each scroll on
// their own.
//
// On a phone it is one pane at a time, the list-detail pattern's compact form
// (Material's canonical layouts, Apple's collapsed split view), and the page
// scrolls as a whole, so Safari's toolbar minimizes as the reader scrolls.
// The header scrolls away with the list, and a tap puts the picked item in
// the list's place, at the top, under a bar that stays on screen to go back
// to the list or step to the row above or below. The list stays mounted,
// hidden, and back returns to the place the reader left it. The open item is
// a history entry, as a chat thread is, so Safari's edge swipe goes back to
// the list as the bar's arrow does. `shown` is the page's: true once a row
// is tapped; `onBack` sets it false.
//
// Either way the list reads on as it scrolls: `onEnd`, while the page has
// another page to read and is not reading one, is called once the list's end
// comes within 600 px of the view.

// The theme's breakpoints are `mobile` and `desktop` (960), not MUI's xs–xl;
// an unknown key is dropped without a word.
export const useDesktop = () =>
  useMediaQuery((theme) => theme.breakpoints.up('desktop'), { noSsr: true })

// Whether a click on a row opens it on a phone: one on the row itself, not on
// a control in it (its play button, a name marked `data-row-action`) nor in a
// menu it opened, whose clicks reach the row through React's portal.
export const opensDetail = (event) =>
  event.currentTarget.contains(event.target) &&
  !event.target.closest('button, [data-row-action]')

// The list's end, watched inside its own scroller on a desktop and in the
// window on a phone. The observer is made again whenever `onEnd` changes, as
// it does when a page lands, so a list still short of the view reads
// another; a null `onEnd` (nothing more, or a read under way) watches
// nothing.
const useEnd = (onEnd, desktop) => {
  const scroller = useRef(null)
  const end = useRef(null)
  useEffect(() => {
    if (!onEnd || !end.current) return undefined
    const observer = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && onEnd(),
      {
        root: desktop ? scroller.current : null,
        rootMargin: '0px 0px 600px 0px',
      }
    )
    observer.observe(end.current)
    return () => observer.disconnect()
  }, [onEnd, desktop])
  return { scroller, end }
}

// On a phone, where the page scrolls as a whole: opening an item keeps the
// list's place and starts the item at the top, as stepping to the next one
// does; back returns to the place. The place is noted as the list scrolls,
// since once the item has replaced the list the page is too short to hold
// it.
const useListPlace = (open, desktop, step) => {
  const place = useRef(0)
  const wasOpen = useRef(open)
  useEffect(() => {
    if (desktop) return undefined
    const note = () => {
      if (!wasOpen.current) place.current = window.scrollY
    }
    window.addEventListener('scroll', note, { passive: true })
    return () => window.removeEventListener('scroll', note)
  }, [desktop])
  useLayoutEffect(() => {
    if (desktop) return
    if (open) window.scrollTo(0, 0)
    else if (wasOpen.current) window.scrollTo(0, place.current)
    wasOpen.current = open
  }, [open, desktop, step])
}

// On a phone, the open item's history entry: the list's own URL again, marked
// in its state. A row's tap pushes it (`useOpenItem`) before the item opens;
// an item opened otherwise gets one as it opens, unless it already stands on
// one, as when back from another page returns to it, so a page opening
// straight onto an item (a link naming it) still goes back to the list. A
// step back that lands off it (Safari's swipe, the browser's back, the bar's
// arrow) closes the item; a replace, as a step to the next row writes, is
// not one. Returns the arrow's back, through history when there is an entry.
export const isItemEntry = (location) => location.state?.splitItem === true

// For a page's row taps on a phone: the item's entry goes in at the tap,
// while the list is still on screen, as a chat thread's does. Safari keeps a
// picture of the screen as an entry is left, for its back swipe to show;
// pushed once the item had replaced the list, the picture was blank.
// `search` is the item's URL when the page names the open item in it.
export const useOpenItem = () => {
  const desktop = useDesktop()
  const navigate = useNavigate()
  const location = useLocation()
  return useCallback(
    (search = location.search) => {
      if (desktop || isItemEntry(location)) return
      navigate(
        { pathname: location.pathname, search },
        { state: { ...location.state, splitItem: true } }
      )
    },
    [desktop, navigate, location]
  )
}

const useItemEntry = (open, desktop, onBack) => {
  const navigate = useNavigate()
  const location = useLocation()
  const navigationType = useNavigationType()
  const marked = isItemEntry(location)
  const entry = useRef(false)
  const lastKey = useRef(location.key)

  useEffect(() => {
    if (desktop || !open || entry.current) return
    entry.current = true
    if (!marked)
      navigate(location, { state: { ...location.state, splitItem: true } })
  }, [desktop, open, marked, location, navigate])

  useEffect(() => {
    if (location.key === lastKey.current) return
    lastKey.current = location.key
    if (navigationType === 'POP' && !marked && entry.current) {
      entry.current = false
      onBack()
    }
  }, [location, navigationType, marked, onBack])

  return useCallback(() => {
    if (!desktop && entry.current) navigate(-1)
    else onBack()
  }, [desktop, navigate, onBack])
}

const Frame = ({ children, mobile }) => (
  <Box
    sx={{
      position: 'relative',
      flex: 1,
      minWidth: 0,
      maxWidth: 1800,
      mx: 'auto',
      display: 'flex',
      flexDirection: 'column',
      ...(mobile ? {} : { height: '100%', overflow: 'hidden', px: 6, pt: 4 }),
    }}
  >
    {children}
  </Box>
)

const BackBar = ({ label, position, onBack, onStep }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
      px: 1,
      py: 0.5,
      borderBottom: 1,
      borderColor: 'divider',
      bgcolor: 'background.default',
    }}
  >
    <Button
      size="small"
      startIcon={<ArrowBackIcon />}
      onClick={onBack}
      sx={{ textTransform: 'none' }}
    >
      {label}
    </Button>
    <Box sx={{ flex: 1 }} />
    {position && position.index >= 0 && (
      <>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
          {position.index + 1} of {position.count}
        </Typography>
        <IconButton
          size="small"
          aria-label="The row above"
          disabled={position.index <= 0}
          onClick={() => onStep(-1)}
        >
          <KeyboardArrowUpIcon />
        </IconButton>
        <IconButton
          size="small"
          aria-label="The row below"
          disabled={position.index >= position.count - 1}
          onClick={() => onStep(1)}
        >
          <KeyboardArrowDownIcon />
        </IconButton>
      </>
    )}
  </Box>
)

const SplitPanes = ({
  header,
  list,
  detail,
  shown,
  onBack,
  onStep,
  position,
  backLabel = 'Back',
  onEnd,
}) => {
  const desktop = useDesktop()
  const { scroller, end } = useEnd(onEnd, desktop)
  const open = Boolean(shown && detail)
  useListPlace(open, desktop, position?.index)
  const back = useItemEntry(open, desktop, onBack)
  if (!desktop) {
    return (
      <Frame mobile>
        <Box sx={{ display: open ? 'none' : 'block', px: 2, py: 3 }}>
          {header}
          {list}
          <Box ref={end} />
        </Box>
        {open && (
          <>
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                // Over the item's outlined labels, which sit at z-index 1;
                // under the menus and dialogs the item opens.
                zIndex: (theme) => theme.zIndex.appBar,
              }}
            >
              <BackBar
                label={backLabel}
                position={position}
                onBack={back}
                onStep={onStep}
              />
            </Box>
            <Box sx={{ p: 2 }}>{detail}</Box>
          </>
        )}
      </Frame>
    )
  }
  return (
    <Frame>
      <Box sx={{ flex: 'none' }}>{header}</Box>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 5fr) minmax(0, 7fr)',
          gap: 2,
        }}
      >
        {/* The padding keeps the picked row's border clear of the
            scrollbar and the last row clear of the window's edge. */}
        <Box
          ref={scroller}
          sx={{ minHeight: 0, overflowY: 'auto', pr: 1, pb: 4 }}
        >
          {list}
          <Box ref={end} />
        </Box>
        <Box sx={{ minHeight: 0, overflow: 'auto', pb: 4 }}>{detail}</Box>
      </Box>
    </Frame>
  )
}

export default SplitPanes
